import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { formatApiError, postInfolettre } from "@/lib/api";

const NewsletterForm = () => {
  const [courriel, setCourriel] = useState("");
  const [consentement, setConsentement] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [confirmation, setConfirmation] = useState("");

  const soumettre = async (e) => {
    e.preventDefault();
    if (!consentement) {
      toast.error("Veuillez consentir à recevoir l'infolettre.");
      return;
    }
    setEnvoi(true);
    setConfirmation("");
    try {
      const res = await postInfolettre({ courriel, consentement });
      setConfirmation(res.message || "Inscription confirmée. Vérifiez votre boîte courriel.");
      setCourriel("");
      setConsentement(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <form onSubmit={soumettre} className="mt-8 border border-espresso-border bg-espresso-2 p-5" data-testid="newsletter-form">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Infolettre</p>
      <p className="mt-3 text-sm leading-relaxed text-ivory/70">
        Recevez nos nouveaux articles et formations par courriel.
      </p>
      <label htmlFor="newsletter-email" className="sr-only">Courriel</label>
      <div className="mt-4 flex">
        <input
          id="newsletter-email"
          type="email"
          required
          value={courriel}
          onChange={(e) => setCourriel(e.target.value)}
          placeholder="votre@courriel.ca"
          className="min-w-0 flex-1 border border-espresso-border bg-espresso px-4 py-3 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold focus:outline-none"
          data-testid="newsletter-email-input"
        />
        <button
          type="submit"
          disabled={envoi}
          className="flex items-center gap-2 bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[hsl(22,83%,40%)] disabled:opacity-60"
          data-testid="newsletter-submit-button"
        >
          <Send size={15} /> {envoi ? "Envoi…" : "OK"}
        </button>
      </div>
      <label className="mt-4 flex items-start gap-3 text-xs leading-relaxed text-ivory/55" data-testid="newsletter-consent-label">
        <input
          type="checkbox"
          checked={consentement}
          onChange={(e) => setConsentement(e.target.checked)}
          className="mt-0.5 accent-primary"
          data-testid="newsletter-consent-checkbox"
        />
        J'accepte de recevoir l'infolettre de la Fondation HORA et je peux me désabonner en tout temps.
      </label>
      {confirmation && <p className="mt-3 text-xs font-medium text-gold" data-testid="newsletter-success-message">{confirmation}</p>}
    </form>
  );
};

export const Footer = () => (
  <footer className="bg-espresso text-ivory" data-testid="site-footer">
    <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 md:grid-cols-12 lg:px-10">
      <div className="md:col-span-5">
        <div className="flex items-center">
          <img src="/logo-hora-dark.png" alt="Fondation HORA" className="h-12 w-auto" data-testid="footer-logo-image" />
        </div>
        <p className="mt-6 max-w-sm text-sm leading-relaxed text-ivory/60">
          Favoriser l'autonomie économique et la résilience des personnes et des organisations
          en démocratisant l'accès à l'éducation, à l'expertise spécialisée, aux ressources et au
          soutien financier.
        </p>
        <NewsletterForm />
      </div>

      <div className="md:col-span-3">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Navigation</p>
        <ul className="mt-5 space-y-3 text-sm text-ivory/70">
          <li><Link to="/a-propos" className="transition-colors hover:text-gold" data-testid="footer-nav-a-propos">À propos</Link></li>
          <li><Link to="/formations" className="transition-colors hover:text-gold" data-testid="footer-nav-formations">Formations</Link></li>
          <li><Link to="/bourses" className="transition-colors hover:text-gold" data-testid="footer-nav-bourses">Bourses</Link></li>
          <li><Link to="/plan-action" className="transition-colors hover:text-gold" data-testid="footer-nav-plan">Plan d'action 2025-2026</Link></li>
          <li><Link to="/blogue" className="transition-colors hover:text-gold" data-testid="footer-nav-blogue">Blogue</Link></li>
          <li><Link to="/impact" className="transition-colors hover:text-gold" data-testid="footer-nav-impact">Notre impact</Link></li>
          <li><Link to="/contact" className="transition-colors hover:text-gold" data-testid="footer-nav-contact">Contact</Link></li>
          <li><Link to="/devenir-partenaire" className="transition-colors hover:text-gold" data-testid="footer-nav-partenaire">Devenir partenaire</Link></li>
          <li><Link to="/devenir-benevole" className="transition-colors hover:text-gold" data-testid="footer-nav-benevole">Devenir bénévole</Link></li>
        </ul>
      </div>

      <div className="md:col-span-4">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Nous joindre</p>
        <ul className="mt-5 space-y-3 text-sm text-ivory/70">
          <li className="flex items-start gap-3"><MapPin size={16} className="mt-0.5 shrink-0 text-gold" /> <span>666, rue Sherbrooke Ouest, bureau 2100<br />Montréal (Québec) H3A 1E7</span></li>
          <li className="flex items-center gap-3"><Mail size={16} className="shrink-0 text-gold" /> info@fondationhora.org</li>
          <li className="flex items-center gap-3"><Phone size={16} className="shrink-0 text-gold" /> <a href="tel:+15148977111" className="transition-colors hover:text-gold" data-testid="footer-phone">514 897-7111</a></li>
        </ul>
        <Link
          to="/don"
          data-testid="footer-donate-button"
          className="mt-8 inline-block rounded-sm bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)]"
        >
          Faire un don
        </Link>
      </div>
    </div>

    <div className="border-t border-espresso-border">
      <div className="mx-auto flex max-w-7xl flex-col gap-2 px-6 py-6 text-xs text-ivory/40 md:flex-row md:items-center md:justify-between lg:px-10">
        <span>© {new Date().getFullYear()} Fondation Hora. Tous droits réservés.</span>
        <span>OSBL — NEQ 1176331792 · Anciennement la Clinique juridique d'affaires</span>
        <span className="flex gap-5">
          <Link to="/conditions-utilisation" className="transition-colors hover:text-gold" data-testid="footer-link-conditions">Conditions d'utilisation</Link>
          <Link to="/politique-confidentialite" className="transition-colors hover:text-gold" data-testid="footer-link-confidentialite">Confidentialité</Link>
        </span>
      </div>
    </div>
  </footer>
);
